import { Controller } from '@hotwired/stimulus'
import { placePopover } from '../place_popover'

// UX S2 chip menu (concept §3.3): one menu of the 10 supplier statuses per list. A status chip of a row opens it;
// a pick submits that row's one attribute to the list's inline save (bulk_update), the server answers with the new
// chip and a toast with „Vrátit“. A tick (boolean cell) submits the same way without the menu.
export default class StatusMenuController extends Controller<HTMLElement> {
  static override targets = ['menu', 'form', 'chip', 'value', 'option', 'more']
  declare readonly menuTarget: HTMLElement
  declare readonly formTarget: HTMLFormElement
  declare readonly chipTarget: HTMLInputElement
  declare readonly valueTarget: HTMLInputElement
  declare readonly optionTargets: HTMLButtonElement[]
  declare readonly moreTarget: HTMLButtonElement

  private opener: HTMLElement | null = null

  open({ currentTarget, params }: { currentTarget: EventTarget | null; params: Record<string, string> }): void {
    if (!(currentTarget instanceof HTMLElement)) return

    this.opener = currentTarget
    this.prepare(params)
    for (const option of this.optionTargets) {
      option.name = params.name ?? ''
      option.setAttribute('aria-checked', String(option.value === params.current))
      // R4 (I-OUX-2): a group (itinerary day) shows its statuses and the current one; „Další stavy…“ the rest.
      option.hidden = Boolean(params.group) && option.dataset.statusGroup !== params.group && option.value !== params.current
    }
    this.moreTarget.hidden = !this.optionTargets.some((option) => option.hidden)
    if (!this.menuTarget.matches(':popover-open')) this.menuTarget.showPopover()
    placePopover(this.menuTarget, currentTarget, 'start')
    const current = this.optionTargets.find((option) => option.value === params.current) ?? this.visibleOptions()[0]
    current?.focus()
  }

  more(): void {
    const first = this.optionTargets.find((option) => option.hidden)
    for (const option of this.optionTargets) option.hidden = false
    this.moreTarget.hidden = true
    first?.focus()
  }

  tick({ currentTarget, params }: { currentTarget: EventTarget | null; params: Record<string, string> }): void {
    if (!(currentTarget instanceof HTMLInputElement)) return

    this.prepare(params)
    this.valueTarget.name = params.name ?? ''
    this.valueTarget.value = currentTarget.checked ? '1' : '0'
    this.formTarget.requestSubmit()
  }

  // ↑/↓ move between the options, like a menu.
  keydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    event.preventDefault()
    const options = this.visibleOptions()
    const index = options.indexOf(document.activeElement as HTMLButtonElement)
    const next = options[(index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length]
    next?.focus()
  }

  hide(): void {
    if (this.menuTarget.matches(':popover-open')) this.menuTarget.hidePopover()
  }

  closed(event: Event): void {
    if (event instanceof ToggleEvent && event.newState === 'closed') this.opener?.focus()
  }

  private visibleOptions(): HTMLButtonElement[] {
    return [...this.optionTargets, this.moreTarget].filter((option) => !option.hidden) // „Další stavy…“ is reachable by ↑/↓
  }

  private prepare(params: Record<string, string>): void {
    this.formTarget.action = params.url ?? ''
    this.valueTarget.name = '' // only a tick sends it; a menu pick sends its option button
    this.chipTarget.value = params.chip ?? ''
  }
}
